import React from 'react';

import {
  GetClientsByIdsItem,
  useGetClientsByIds,
} from '../data/useGetClientsById';
import {
  SearchJobsWithTasksResult,
  useSearchJobsWithTasks,
} from '../data/useSearchJobsWithTasks';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../data/useGetMembersList';
import {
  QuerySearchJobsArgs,
  searchJobsArgsToGqlSearchJobsArgs,
} from '../types/mapper';
import { TaskStatusEnumValue } from '../components/work/types/task-status-enum';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../data/useGetJobStatusList';

import { getVisibleStatusesFromJobStatuses } from './use-get-visible-job-statuses';

export type CountByTaskStatus = Record<TaskStatusEnumValue, number | undefined>;
export interface JobWithTasks extends SearchJobsWithTasksResult {
  client?: GetClientsByIdsItem;
  partner?: GetMemberListItem;
  manager?: GetMemberListItem;
  staff?: GetMemberListItem;
  assignee?: GetMemberListItem;
  taskCountByStatus: CountByTaskStatus;
  status: GetJobStatusListResult;
}

/**
 * make sure it is getting exactly what is needed from the GQL
 */
export const useQueryJobsWithTasks = ({
  variables,
  skip,
}: {
  variables: QuerySearchJobsArgs;
  skip?: boolean;
}): {
  loading?: boolean;
  error?: boolean;
  data?: JobWithTasks[];
} => {
  const statuses = useGetJobStatusList({ skip: Boolean(skip) });
  const visibleStatuses = React.useMemo(
    () => getVisibleStatusesFromJobStatuses(statuses.data),
    [statuses.data]
  );
  const members = useGetMembersList({ skip });

  const options =
    visibleStatuses && visibleStatuses.length > 0
      ? {
          variables: searchJobsArgsToGqlSearchJobsArgs(
            variables,
            visibleStatuses
          ),
          skip,
        }
      : { skip: true };

  const jobs = useSearchJobsWithTasks(options);

  const clientIds = [
    ...new Set(
      jobs.data
        ?.map(({ clientId }) => clientId)
        .filter(<T>(x: T): x is NonNullable<T> => Boolean(x))
    ),
  ];

  const clients = useGetClientsByIds(
    !skip && clientIds && clientIds.length > 0
      ? {
          variables: { cloudIds: clientIds },
        }
      : { skip: true }
  );

  return React.useMemo(() => {
    if (clients.loading || jobs.loading || members.loading || statuses.loading)
      return { loading: true };
    if (clients.error || jobs.error || members.error || statuses.error)
      return { error: true };

    const data = jobs.data?.map((job) => {
      const status = statuses.data?.find((s) => s.cloudId === job.jobStatusId);
      if (!status) {
        console.error(
          `Query Jobs With Tasks: unable to find status "${job.jobStatusId}" for job "${job.cloudId}"`
        );
      }
      const client = clients.data?.find(
        (client) => client.cloudId === job.clientId
      );

      // Reduce syntax is horrible, but this is nice and concise
      const taskCountByStatus =
        job.tasks?.reduce((result, task) => {
          result[task.status as TaskStatusEnumValue] =
            (result[task.status as TaskStatusEnumValue] || 0) + 1;
          return result;
        }, {} as CountByTaskStatus) || {};

      const partner = members.data?.find(
        (member) => member.cloudId === job.partnerUserId
      );
      const manager = members.data?.find(
        (member) => member.cloudId === job.managerUserId
      );
      const staff = members.data?.find(
        (member) => member.cloudId === job.staffUserId
      );
      const assignee = members.data?.find(
        (member) => member.cloudId === job.assigneeUserId
      );
      return {
        ...job,
        client,
        partner,
        manager,
        staff,
        assignee,
        taskCountByStatus,
        status: status!, // the status absolutely will exist. If not, things are badly broken
      };
    });

    return { data };
  }, [clients, jobs, members, statuses]);
};
